import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calculator, TrendingUp, DollarSign, Clock, Users, Zap } from 'lucide-react';

interface CalculatorInputs {
  currentManualCost: number;
  automationPotential: number;
  implementationCost: number;
  timeToImplement: number;
  teamSize: number;
  industry: string;
}

interface CalculatorResults {
  annualSavings: number;
  paybackPeriod: number;
  threeYearROI: number;
  efficiencyGain: number;
  costReduction: number;
}

const ROICalculator: React.FC = () => {
  const [inputs, setInputs] = useState<CalculatorInputs>({
    currentManualCost: 100000,
    automationPotential: 70,
    implementationCost: 50000,
    timeToImplement: 6,
    teamSize: 5,
    industry: 'technology'
  });

  // Track when users view the ROI Calculator
  React.useEffect(() => {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'roi_calculator_viewed', {
        event_category: 'engagement'
      });
    }
  }, []);

  const [results, setResults] = useState<CalculatorResults | null>(null);
  const [isCalculated, setIsCalculated] = useState(false);

  const industries = [
    { value: 'technology', label: 'Technology' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'finance', label: 'Financial Services' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'ecommerce', label: 'E-commerce' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'retail', label: 'Retail' },
    { value: 'other', label: 'Other' }
  ];

  const calculateROI = () => {
    const annualSavings = (inputs.currentManualCost * (inputs.automationPotential / 100));
    const paybackPeriod = inputs.implementationCost / annualSavings;
    const threeYearROI = ((annualSavings * 3 - inputs.implementationCost) / inputs.implementationCost) * 100;
    const efficiencyGain = inputs.automationPotential;
    const costReduction = ((inputs.currentManualCost - annualSavings) / inputs.currentManualCost) * 100;

    setResults({
      annualSavings,
      paybackPeriod,
      threeYearROI,
      efficiencyGain,
      costReduction
    });
    setIsCalculated(true);

    // Track ROI Calculator usage
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'roi_calculator_used', {
        event_category: 'engagement',
        event_label: inputs.industry,
        value: Math.round(annualSavings)
      });
    }
  };

  const handleInputChange = (field: keyof CalculatorInputs, value: number | string) => {
    setInputs(prev => ({ ...prev, [field]: value }));
    setIsCalculated(false);
  };

  const resetCalculator = () => {
    setInputs({
      currentManualCost: 100000,
      automationPotential: 70,
      implementationCost: 50000,
      timeToImplement: 6,
      teamSize: 5,
      industry: 'technology'
    });
    setResults(null);
    setIsCalculated(false);
  };

  return (
    <section id="roi-calculator" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Technology ROI Calculator
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Calculate the potential return on investment for your AI/ML and automation initiatives. 
            Get instant insights into cost savings, payback periods, and efficiency gains.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Input Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-2xl shadow-xl p-8"
          >
            <div className="flex items-center mb-6">
              <Calculator className="w-8 h-8 text-blue-600 mr-3" />
              <h3 className="text-2xl font-bold text-gray-900">Project Parameters</h3>
            </div>

            <div className="space-y-6">
              {/* Current Manual Cost */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Annual Manual Processing Cost ($)
                </label>
                <input
                  type="number"
                  value={inputs.currentManualCost}
                  onChange={(e) => handleInputChange('currentManualCost', Number(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="100000"
                />
              </div>

              {/* Automation Potential */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Automation Potential (%)
                </label>
                <input
                  type="range"
                  min="10"
                  max="95"
                  value={inputs.automationPotential}
                  onChange={(e) => handleInputChange('automationPotential', Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-sm text-gray-500 mt-1">
                  <span>10%</span>
                  <span className="font-medium text-blue-600">{inputs.automationPotential}%</span>
                  <span>95%</span>
                </div>
              </div>

              {/* Implementation Cost */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Implementation Cost ($)
                </label>
                <input
                  type="number"
                  value={inputs.implementationCost}
                  onChange={(e) => handleInputChange('implementationCost', Number(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="50000"
                />
              </div>

              {/* Time to Implement */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Time to Implement (months)
                </label>
                <input
                  type="number"
                  min="1"
                  max="24"
                  value={inputs.timeToImplement}
                  onChange={(e) => handleInputChange('timeToImplement', Number(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="6"
                />
              </div>

              {/* Team Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Size (people)
                </label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={inputs.teamSize}
                  onChange={(e) => handleInputChange('teamSize', Number(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="5"
                />
              </div>

              {/* Industry */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Industry
                </label>
                <select
                  value={inputs.industry}
                  onChange={(e) => handleInputChange('industry', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {industries.map((industry) => (
                    <option key={industry.value} value={industry.value}>
                      {industry.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  onClick={calculateROI}
                  className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center"
                >
                  <TrendingUp className="w-5 h-5 mr-2" />
                  Calculate ROI
                </button>
                <button
                  onClick={resetCalculator}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors duration-200"
                >
                  Reset
                </button>
              </div>
            </div>
          </motion.div>

          {/* Results */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-6"
          >
            {!isCalculated ? (
              <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
                <div className="text-gray-400 mb-4">
                  <Calculator className="w-16 h-16 mx-auto" />
                </div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  Ready to Calculate
                </h3>
                <p className="text-gray-500">
                  Fill in your project parameters and click "Calculate ROI" to see your potential returns.
                </p>
              </div>
            ) : (
              <>
                {/* Annual Savings */}
                <div className="bg-white rounded-2xl shadow-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <DollarSign className="w-8 h-8 text-green-600 mr-3" />
                      <h3 className="text-xl font-bold text-gray-900">Annual Savings</h3>
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-green-600">
                    ${results?.annualSavings.toLocaleString()}
                  </div>
                  <p className="text-gray-600 mt-2">
                    Expected annual cost reduction from automation
                  </p>
                </div>

                {/* Payback Period */}
                <div className="bg-white rounded-2xl shadow-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <Clock className="w-8 h-8 text-blue-600 mr-3" />
                      <h3 className="text-xl font-bold text-gray-900">Payback Period</h3>
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-blue-600">
                    {results?.paybackPeriod.toFixed(1)} months
                  </div>
                  <p className="text-gray-600 mt-2">
                    Time to recover implementation costs
                  </p>
                </div>

                {/* 3-Year ROI */}
                <div className="bg-white rounded-2xl shadow-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <TrendingUp className="w-8 h-8 text-purple-600 mr-3" />
                      <h3 className="text-xl font-bold text-gray-900">3-Year ROI</h3>
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-purple-600">
                    {results?.threeYearROI.toFixed(0)}%
                  </div>
                  <p className="text-gray-600 mt-2">
                    Return on investment over 3 years
                  </p>
                </div>

                {/* Efficiency Metrics */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-2xl shadow-xl p-6">
                    <div className="flex items-center mb-3">
                      <Zap className="w-6 h-6 text-orange-600 mr-2" />
                      <h4 className="font-semibold text-gray-900">Efficiency Gain</h4>
                    </div>
                    <div className="text-2xl font-bold text-orange-600">
                      {results?.efficiencyGain}%
                    </div>
                  </div>
                  <div className="bg-white rounded-2xl shadow-xl p-6">
                    <div className="flex items-center mb-3">
                      <Users className="w-6 h-6 text-indigo-600 mr-2" />
                      <h4 className="font-semibold text-gray-900">Cost Reduction</h4>
                    </div>
                    <div className="text-2xl font-bold text-indigo-600">
                      {results?.costReduction.toFixed(0)}%
                    </div>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </div>

        {/* Additional Insights */}
        {isCalculated && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white"
          >
            <h3 className="text-2xl font-bold mb-4">💡 Key Insights</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h4 className="font-semibold mb-2">Implementation Strategy</h4>
                <p className="text-blue-100">
                  Consider a phased approach to minimize risk and demonstrate early value.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Team Impact</h4>
                <p className="text-blue-100">
                  Automation can free up {inputs.teamSize} team members for higher-value work.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Risk Mitigation</h4>
                <p className="text-blue-100">
                  Start with pilot projects to validate assumptions before full-scale implementation.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default ROICalculator;
